// Type aliases
export type Timestamp = number // Unix timestamp in milliseconds

export const KNOWN_ACTOR_IDS = [
    'ME',
    'DELETED',
    'UNKNOWN',
    'ANONYMOUS', // https://developers.google.com/workspace/drive/activity/v2/reference/rest/v2/activity/actor#Actor
    'SYSTEM', // Event triggered by system operations instead of end users. (TYPE_UNSPECIFIED, USER_DELETION, TRASH_AUTO_PURGE)
    'ADMIN',
] as const

export type ActorId = typeof KNOWN_ACTOR_IDS[number] | string // 113109242499701404173 ('people/113109242499701404173')
// = 'ME'
//     | 'DELETED'
//     | 'UNKNOWN'
//     | 'ANONYMOUS'
//     | 'SYSTEM'
//     | 'ADMIN'
//     | string

export type GoogleItemId = string

// Action types
export type GActionType
    = 'CREATE'
        | 'EDIT'
        | 'MOVE'
        | 'RENAME'
        | 'DELETE'
        | 'RESTORE'
        | 'PERMISSION_CHANGE'
        | 'COMMENT'
        | 'DLP_CHANGE'
        | 'REFERENCE'
        | 'SETTINGS_CHANGE'
        | 'APPLIED_LABEL_CHANGE'
        | 'OTHER'

export type GActionSubtype
    = 'CREATE_NEW'
        | 'CREATE_UPLOAD'
        | 'CREATE_COPY'
        | 'COMMENT_POST'
        | 'COMMENT_ASSIGNMENT'
        | 'COMMENT_SUGGESTION'
        | 'OTHER'

export type GActionCommentType
    = 'UNSPECIFIED'
        | 'ADDED'
        | 'DELETED'
        | 'REPLY_ADDED'
        | 'REPLY_DELETED'
        | 'RESOLVED' // for POST and ASSIGNMENT actions
        | 'REOPENED' // for POST and ASSIGNMENT actions
        | 'REASSIGNED' // for ASSIGNMENT
        | 'ACCEPTED' // for SUGGESTION
        | 'REJECTED' // for SUGGESTION
        | 'ACCEPT_DELETED' // for SUGGESTION
        | 'REJECT_DELETED' // for SUGGESTION
        // TODO: Google DOESN'T log EDITED action for comments/replies
        //       the only way to check this is to fetch driveApi.comments.get() and compare 'modifiedTime'

// what action types to track for folders
export type GParentFolderActionType
    = 'RENAME'
        | 'MOVE'
        | 'DELETE'
        // | 'RESTORE'

export interface GPeopleApiPerson {
    displayName?: string // Funtik Burlaca
    familyName?: string  // Burlaca
    givenName?: string   // Funtik

    photo?: string // the url of the photo extracted from People API response
    isDefaultPhoto?: boolean // whether the photo is the default one provided by Google
                             // it means the photo is not a custom one uploaded by the user
                             // and the "commentAuthor" will have higher priority over the default photo
    email?: string // the email address extracted from People API response

    updated?: Timestamp // when this info was last updated
}

export interface GPerson {
    personId: ActorId // ME|SYSTEM|113109242499701404173 etc.

    displayName?: string // Funtik Burlaca
    familyName?: string // Burlaca
    givenName?: string // Funtik
    initials?: string // FB: to be used in avatars when there is no photo available

    email?: string // funtik@burlaca.com
    photo?: string // https://example.com/photo.jpg

    bgColor: string
    useInitialsAsAvatar: boolean // whether to use initials as avatar (if no photo available).
                                 // true for known actor IDs and basic placeholder persons.

    peopleApiPerson?: GPeopleApiPerson // the url of the photo extracted from People API response
    commentAuthor?: GPeopleApiPerson   // extracted from Drive.comments.get() response and
                                       // matched by comment/reply.createdTime
                                       // (not by email because it might be missing or different)
    raw?: string // 'json response from People API'
    updated?: Timestamp // when this info was last updated
}

export type PersonMap = Map<ActorId, GPerson>

export interface Avatar {
    actor: ActorId
    // ts: Timestamp
    // commentsForMeCount: number
    // hasComments: boolean
    updates: ActorUpdates
    person?: GPerson
    ts: Timestamp
    // useInitialsAsAvatar: boolean
    // displayName?: string
    // givenName?: string // Funtik
    // initials?: string
    // photo?: string
    // bgColor?: string
}

// Main action interface
export interface GFileAction {
    id?: number // primary key for Dexie: autogenerated

    folderId: number
    gid: GoogleItemId
    action: GActionType
    subAction?: GActionSubtype
    title: string
    mimeType: string
    isFolder: boolean

    icon: string

    // TODO: CREATE_COPY details: originalItem
    createCopy?: GFileCreateCopy // for CREATE COPY actions
    move?: GFileMoveData // for MOVE actions
    oldTitle?: string // for RENAME actions
    status?: 'DELETED' | 'RESTORED' // for DELETE/RESTORE actions
    permissions?: GFilePermissionChange // for PERMISSION_CHANGE actions
    comment?: GActionCommentData // for COMMENT actions

    // TODO: owner. do we need it?

    // TODO: DataLeakPrevention: Flagged/Cleared
    // TODO: referenceChanges
    // TODO: settingsChanges
    // TODO: appliedLabelChanges

    actor: ActorId
    actorImpersonated?: boolean // if action was done on behalf of another user

    ts: Timestamp
    tsRaw: string // raw RFC3339 timestamp
}

export interface GFileCreateCopy {
    // TODO: add fields https://developers.google.com/workspace/drive/activity/v2/reference/rest/v2/activity/targetreference#TargetReference
    driveItem: any // gapi.client.driveactivity.DriveItem
    drive: any // gapi.client.driveactivity.Drive
}

export interface GFileMoveData {
    added?: GFolder
    removed?: GFolder
}

export interface GFolder {
    gid: GoogleItemId
    title: string // it's empty for MY_DRIVE_ROOT
    type: 'TYPE_UNSPECIFIED' | 'STANDARD_FOLDER' | 'SHARED_DRIVE_ROOT' | 'MY_DRIVE_ROOT'
    // TODO: check if it can be a "drive: DriveReference"
    // https://developers.google.com/workspace/drive/activity/v2/reference/rest/v2/activity/targetreference#TargetReference
}

export interface GActionCommentData {
    commentId: string
    replyId?: string
    replyIdOriginal?: string
    type: GActionCommentType
    assignedTo?: ActorId
    mentioned?: ActorId[]
}

export type GPermissionRole
    = 'ROLE_UNSPECIFIED'
        | 'OWNER'
        | 'ORGANIZER'
        | 'FILE_ORGANIZER'
        | 'EDITOR'
        | 'COMMENTER'
        | 'VIEWER'
        | 'PUBLISHED_VIEWER'

export type GPermissionScope
    = 'USER'
        | 'GROUP'
        | 'DOMAIN'
        | 'ANYONE'

export interface GPermissionGroup {
    email?: string
    title?: string
}

export interface GFilePermissionChangeDetail {
    role: GPermissionRole
    allowDiscovery?: boolean
    scope: GPermissionScope
    user?: ActorId // for 'USER' type
    domain?: string // for 'DOMAIN' type
    group?: GPermissionGroup // for 'GROUP' type
}

export interface GFilePermissionChange {
    added?: GFilePermissionChangeDetail[]
    removed?: GFilePermissionChangeDetail[]
}

// Action and Actor tracking
export interface GFolderLastAction {
    action: GActionType
    subAction?: GActionSubtype
    ts: Timestamp
    actor: ActorId
}

export type GActorsTimeMap = Map<ActorId, Timestamp>
export type GFilesActorsTimeMap = Map<GoogleItemId, GActorsTimeMap> // Map<fileGid, Map<actorId, lastActionTs>>

export interface GActorTimestamp {
    actor: ActorId
    ts: Timestamp
}

export interface GActorActionsInfo {
    actor: ActorId
    // TODO: assignmentCount? assignedToMeCount?
    commentCount: number // number of COMMENT actions
    editCount: number // number of EDIT actions
    actionCount: number // total number of actions
    lastTs: Timestamp // last action timestamp
}

export interface GLastTarget {
    gid: GoogleItemId
    itemName: string
    mime: string
}

export type SyncJobStatus
    = 'PENDING'
        | 'WORKING'
        | 'COMPLETED'
        | 'ERROR'
        | 'FAILED'    // after 3 retries (status=ERROR and retryCount >= 3)

export interface SyncJob {
    pageToken?: string // for Drive Activity API pagination
    fromTs: Timestamp
    toTs: Timestamp
    status: SyncJobStatus
    // startedAt: Timestamp
    // completedAt?: Timestamp
    retryCount: number
    errorMessage?: string
}

// Progress feedback for the sync progress dialog, updated per fetched page
export interface SyncProgress {
    fromTs: Timestamp
    toTs: Timestamp
    currentTs: Timestamp // ts of the first action in the most recently fetched page
    fileCount: number // distinct GFileAction.gid seen so far
    personCount: number // distinct GFileAction.actor seen so far
    actionCount: number
}

// Progress feedback for the file sync progress dialog, one entry per stage of [fileGid].vue's fetchFile()
export type FileSyncStage = 'meta' | 'location' | 'comments'

export interface FileSyncProgress {
    stage: FileSyncStage
    commentIndex?: number // current comment being fetched (1-based), for the 'comments' stage
    commentTotal?: number // total comments being fetched, for the 'comments' stage
}

export type RootKind = 'myDrive' | 'sharedDrive' | 'sharedFolder'

// Main interfaces
export interface Folder {
    id?: number // primary key for Dexie: automatically generated when adding to Dexie.

    rootKind: RootKind
    folderColorRgb: string // RGB color value

    gid: GoogleItemId // unique key (google Id)
    parentGid?: GoogleItemId
    itemName: string // ex: 0B3iOB6b1WnwaTi1nYWJDek01amM
    trashed: boolean
    trashedTime: Timestamp // Timestamp

    actors: ActorId[] // for filtering in Dexie and displaying in Folders view
    actorsLastActionTime: GActorTimestamp[]

    lastAction?: GFolderLastAction
    lastTarget?: GLastTarget
    fromTs: Timestamp // first action.ts
    toTs: Timestamp // last action.ts
    prevSyncTs?: Timestamp // previous toTs, for showing changes since the last sync

    syncTime: Timestamp // last time when folder has been synced
    neverSynced: boolean // true until the folder is synced for the first time
    isSyncing: boolean // indicates if folder is currently syncing

    markedReadTime: Timestamp // last time when folder was marked as read
    markedAsRead: boolean

    fileCount: number // number of files
    unreadFileCount: number // number of unread files

    syncJob?: SyncJob // populated when a sync is in progress
    // markedReadTime: Timestamp // no need, do fromTs = toTs & fetch(fromTs, now)

    updates: FolderUpdates
    actorsUpdates: ActorsUpdatesList

    active: boolean // only 3 folders can be active in the Free plan
}

// ActivityAPI.query() doesn't return file.folderId
export interface GParent {
    gid: GoogleItemId // '11y3FspGUNv241qyRFDh9ZgSbQoJje3td'
    parentGid: GoogleItemId // '12Z3DagLJ84TT5xTTYvW98L6nyuRIRiwT'
    itemName: string // ex: 0B3iOB6b1WnwaTi1nYWJDek01amM
    folderColorRgb: string // RGB color value
    lastAction: Timestamp // Timestamp - periodically clean 'fileParent' table DELETE WHERE lastAction > 3 months
    // colorRgb: string // for displaying in the UI
    // "shared": true,
    // "sharedWithMeTime": "2018-07-11T21:12:50.016Z",
    //  "spaces": ["drive"],
    // "webViewLink": "https://drive.google.com/drive/folders/1GqRbCHVjP5h1X-BMoisn-KMlnASB3KOz",
    //   "sharingUser": {
    //     "displayName": "Oleg Burlaca",
    //     "emailAddress": "oburlaca@gmail.com",
    //     "kind": "drive#user",
    //     "me": false,
    //     "permissionId": "10637606104002704615",
    //     "photoLink": "https://lh3.googleusercontent.com/a-/ALV-UjUCQXg_ExEoSzE_G4gmaGaFifuPWN7Gnfu_oSgx-xaJ0uB9Z0pB=s64"
    //   },
    //   "owners": [
    //     {
    //       "displayName": "Oleg Burlaca",
    //       "emailAddress": "oburlaca@gmail.com",
    //       "kind": "drive#user",
    //       "me": false,
    //       "permissionId": "10637606104002704615",
    //       "photoLink": "https://lh3.googleusercontent.com/a-/ALV-UjUCQXg_ExEoSzE_G4gmaGaFifuPWN7Gnfu_oSgx-xaJ0uB9Z0pB=s64"
    //     }
    //   ],
}

export type ArcType
    = 'ASSIGNMENT_NEW_ME'           // purple color: for new assignments when I'm the assignee
        | 'ASSIGNMENT_REOPEN_ME' // orange color: for reopened assignments when I'm the assignee
        | 'ASSIGNMENT_RESOLVED_ME' // green color: for resolved assignments when I'm the assignee
        // | 'SUGGESTION'       // orange color: for new suggestions when I'm the file owner
        | 'MENTION_ME'          // blue color: for new comments when I'm mentioned in the comment
        | 'WATCH'          // dark gray color: for comments/replies/assignments that I'm watching
        | 'OTHER'            // light gray color: all other comments/replies/assignments that I'm not watching

export const ArcTypePriority: Record<ArcType, number> = {
    ASSIGNMENT_NEW_ME: 5,
    ASSIGNMENT_REOPEN_ME: 4,
    ASSIGNMENT_RESOLVED_ME: 3,
    // SUGGESTION: 2,
    MENTION_ME: 1,
    WATCH: 0,
    OTHER: -1,
}

export const ARC_COLORS: Record<ArcType, string> = {
    ASSIGNMENT_NEW_ME: '#9c27b0',  // new
    ASSIGNMENT_REOPEN_ME: '#fd7e14',
    ASSIGNMENT_RESOLVED_ME: '#2f9e44',
    MENTION_ME: '#228be6',
    WATCH: 'rgb(var(--v-theme-arc-watch))',
    OTHER: 'rgb(var(--v-theme-arc-other))',
}

// for Folder and Person
export interface AvatarArc {
    gid: GoogleItemId
    //  replyCount?: number across all comments (TODO: that I'm watching in this file or all comments?)
    type: ArcType
    forMe: boolean // whether this comment/reply/assignment is relevant for me
                     // (e.g. assigned/mentioned or simply @watching the thread)
                     // gray color for all ring types.
    ts: Timestamp // last update for `gid` file. new events overwrite old ones if ring type has higher priority
                  // (e.g. ASSIGNMENT >  SUGGESTION > COMMENT > REPLY) or ts is newer. used for coloring the rings.
}

export type CommentId = string // commentId can be either a comment or a reply ID, because both of them can have ASSIGNMENT/SUGGESTION/COMMENT actions that we want to track with rings.

export interface FileArc {
    commentId?: CommentId  // comment/suggestion ID (NOT a reply)
    //  replyCount?: number
    type: ArcType
    forMe: boolean
    ts: Timestamp // last update for `gid` file. new events overwrite old ones if ring type has higher priority
                  // (e.g. ASSIGNMENT >  SUGGESTION > COMMENT > REPLY) or ts is newer. used for coloring the rings.
}

export interface FolderUpdates {
    newFiles: number // number of new (upload, create, restore) files
    delFiles: number // number of deleted files
    posts: number // number of total posts (comments+replies) from all files (including resolved)
    ts: Timestamp // last update timestamp, used for sorting

    ring: AvatarArc[]  // up to 25 rings for displaying in the UI. Sorted by ring type
}

// for in-app calculations, not stored in Dexie (can't store Map)
export interface FolderUpdatesWithRingMap extends FolderUpdates {
    ringMap: Map<GoogleItemId, AvatarArc>
}

export interface ActorUpdates extends FolderUpdates {
    actorId: ActorId
}

export interface ActorUpdatesWithRingMap extends ActorUpdates {
    ringMap: Map<GoogleItemId, AvatarArc>
}

export type ActorsUpdatesMap = Map<ActorId, ActorUpdatesWithRingMap> // ActorUpdatesWithRingMap
export type ActorsUpdatesList = ActorUpdates[] // List of ActorUpdates sorted by priority (ring type) and last update time

// export type PersonAvatar = {
//     personId: ActorId
//     displayName?: string

export interface FileUpdates {
    posts: number // number of total posts (comments+replies) from all files (including resolved)
    ring: FileArc[]

    // true if at least one `ring` entry has `forMe` true
    forMe: boolean
}

// export interface FileUpdatesWithRingMap extends FileUpdates {
//     ringMap: Map<CommentId, FileArc>
// }

// export type FilesUpdatesMap = Map<GoogleItemId, FileUpdates> // Map<fileGid, FileUpdatesWithRingMap>

export interface GFile {
    // primary key: combination of folderId + gid, because the same file can exist in multiple folders
    folderId: number
    gid: GoogleItemId // Primary key: '11y3FspGUNv241qyRFDh9ZgSbQoJje3td'

    isRead: boolean // whether the file has been seen by the user
    deleted: boolean

    title: string // 'Document title.txt'
    oldTitle?: string // for RENAME actions. Holds the very first title before rename (the oldest one)

    // lastAction: GFileLastAction
    action: GActionType // Google Activity API ActionDetail
    subAction?: GActionSubtype

    // TODO: actions&subActions should NOT include my own actions?
    actions: GActionType[] // index 'actions*', for filtering
    subActions: GActionSubtype[] // for a finer filter: 'subActions: [...]'

    actor: ActorId // last actor who modified the file
    ts: Timestamp // Timestamp of the last action, used for sorting
    tsRaw: string // raw RFC3339 timestamp

    /*
    trashed: boolean // for TRASH/RESTORE actions. Holds the very fist trashed state (if there were several trash/restore actions)
    trashedTime: number // Timestamp
    */

    /* TODO: DataLeakPrevention: Flagged/Cleared */
    /* TODO: ApplicationReference: LINK/DISCUSS */
    /* TODO: restrictionChanges */
    /* TODO: AppliedLabelChange: label added/removed */
    /* TODO: permissionChange */

    mimeType: string
    icon: string // cached icon type

    onlyMeAsActor: boolean // displaying 'My Actions Only' files
    actors: ActorId[] // for filtering, sorted by last action time DESC

    actorsLastEditTime: GActorTimestamp[] // displaying LastEditors
    actorsLastActionTime: GActorTimestamp[]

    commentsApiCheckTs: Timestamp // last time comments were checked/fetched from Drive API, used to throttle API calls

    updates: FileUpdates

    // comment/reply related
    /*
    assignedToMe: string[] // Comment IDs (can be comment||reply)
    mentionsMe: string[] //
    assignedToOthers: string[]
    mentionsOthers: string[]
    */
}

export function createGFile (folderId: number, gid: GoogleItemId): GFile {
    return {
        folderId,
        gid,
        title: '',
        action: 'OTHER',
        subAction: 'OTHER',
        actor: '',
        ts: 0,
        tsRaw: '',
        icon: '',
        // trashed: false,
        // trashedTime: 0,
        mimeType: '',
        isRead: false,
        deleted: false,
        actions: [],
        subActions: [],
        onlyMeAsActor: false,
        actors: [],
        actorsLastActionTime: [],
        actorsLastEditTime: [],
        commentsApiCheckTs: 0,
        updates: {
            posts: 0,
            ring: [],
            forMe: false,
        },
        /*
        assignedToMe: [],
        mentionsMe: [],
        assignedToOthers: [],
        mentionsOthers: [],
        */
    }
}

// Metadata interfaces
export interface GImageMediaMetadata {
    // flashUsed?: boolean
    // meteringMode?: string
    // sensor?: string
    // exposureMode?: string
    // colorSpace?: string
    // whiteBalance?: string
    width?: number
    height?: number
    location?: {
        latitude: number
        longitude: number
        altitude: number
    }
    rotation?: number
    time?: string
    cameraMake?: string
    cameraModel?: string
    // exposureTime?: number
    // aperture?: number
    // focalLength?: number
    // isoSpeed?: number
    // exposureBias?: number
    // maxApertureValue?: number
    // subjectDistance?: number
    // lens?: string
}

export interface GVideoMetadata {
    width: number
    height: number
    durationMillis: string // string (int64 format)
}

export interface GFileMetadata {
    // folderId: number // it doesn't matter which folder it belongs to,
    // because metadata fetched onFileOpen but not the meta from the folder.Period
    gid: GoogleItemId // '11y3FspGUNv241qyRFDh9ZgSbQoJje3td'

    title: string
    mimeType: string

    parentGid?: GoogleItemId // filled onFileExpand: moved to `fileParent` table
    createdTime: Timestamp
    // description?: string
    hasThumbnail: boolean
    starred: boolean
    modifiedTime: Timestamp
    viewedByMeTime: Timestamp
    sharedWithMeTime?: Timestamp
    // owners: GPerson[]
    // lastModifyingUser?: GPerson
    shared: boolean
    image?: GImageMediaMetadata // imageMediaMetadata
    video?: GVideoMetadata
    size: number // (int64 format)
    ts: Timestamp // the Timestamp when the metadata was fetched, used for cache expiration

    thumbnailLink?: string
    webContentLink?: string // "https://drive.google.com/uc?id=1FwKFZvPn0hOc9KIV3IvTdpa28Ju5jUYp&export=download",
    webViewLink?: string // "https://drive.google.com/file/d/1FwKFZvPn0hOc9KIV3IvTdpa28Ju5jUYp/view?usp=drivesdk",

    // TODO: add shortcutDetails
    // if "mimeType": "application/vnd.google-apps.shortcut",
    // then add "shortcutDetails": { "targetId": string, "targetMimeType": string }
}

// export type TreeUpdateMap = Map<GoogleItemId, GFileAction[]>

export type GCommentType
    = 'POST'
        | 'ASSIGNMENT'
        | 'SUGGESTION'

export interface GCommentDataAuthor {
    displayName: string
    emailAddress: string
    photoLink: string
    me: boolean
}

// populated from Drive Activity API
export interface GComment {
    // primary key: id = folderId + fileGid + commentId
    folderId: number
    fileGid: GoogleItemId
    commentId: CommentId

    arcType: ArcType // the most important arc type for this comment thread, used for coloring and sorting in the UI
    arcModifiedTs: Timestamp // the timestamp when the topArcType was last modified

    // topArcForMe: boolean // whether the topArcType is relevant for me (e.g. I'm assigned/mentioned or simply @watching the thread)

    watchingTs: Timestamp // the timestamp when the watching status was last updated
    watching: boolean // whether I'm watching this comment thread
                      // (either assigned/mentioned or simply @watching the thread)
                      // set `watching = true` if I'm assigned or mentioned in the comment or any of its replies

    // TODO: implement later: use DriveAPI comments.get() and use comment.createdDate to call
    // Drive Activity API query with: itemName=<fileGid>, fromDate = comment.createdDate && TYPE=COMMENT
    // then populate `firstAssignor`, `assignor`, `assigneeIsMe`
    needsActivityFetch: boolean // whether we need to fetch the comment data from Drive Activity API
                                // may happen when the Comment was added long time ago and only replies are fetched
    ghost: boolean // true when this comment thread itself was never seen as an 'ADDED' activity
                  // (only reconstructed from REPLY activities), so its own comment-level data is unknown
    hasGhostReplies?: boolean // whether this comment thread has replies that are "ghosts" (fetched directly by Drive API when a file has replies newer than folder.toTs)
    type: GCommentType

    lastActionType: GActionCommentType // last action type for this comment thread, used for sorting and filtering
    lastReopenedTs: Timestamp // the timestamp when the comment thread was last reopened
    lastReassignedTs: Timestamp // the timestamp when the comment thread was last reassigned

    open: boolean // whether the comment thread is open or resolved
    deleted: boolean

    currentAssignor?: ActorId // the one who made the last re-assignment in the thread
                              // currentAssignor = reopener. i.e. the responsible person becomes the one who reopens.
    currentAssignee?: ActorId // the current assignee.

    actor: ActorId // who assigned. the first assignor who created the Comment Thread
    assignee?: ActorId // who is assigned to. (the first one)
    resolver?: ActorId // who resolved the comment.

    mentioned?: ActorId[]  // mentioned in the comment content,
                           // TODO: add `mentionedOverall` for mentioned in the comment or any of its replies
    mentionsMe: boolean // TODO: whether I'm mentioned in this comment thread (either in the comment or any of its replies) ???

    createdTs: Timestamp // from Drive Activity API
    modifiedTs: Timestamp // data form Drive Activity API, NOT from Drive API. see `modifiedApiTs` for the Drive API modified time.
                          // on DriveAPI:comments.get: update comment.data,
                          // replies = db.replies.where('createdTs').within(folder.fromTs, folder.toTs).toArray()
                          // replies.setData from DriveAPI:comment.replies[]
                          // i.e. update only the replies we have from Drive Activity API
                          // comment.modifiedTs = max(comment.modifiedTs, replies[].data.modifiedTime, comment.data.modifiedTime)
                          // with the condition that it's not older than folder.toTs

                          // if comment.data.modifiedTime > folder.toTs:
                          // means there are new replies/some replies were EDITED/comment itself was edited
                          // (EDIT actions don't generate new activity in Drive Activity API)
                          // in such cases, show a '...' in the UI to indicate that there are new updates in this comment thread

    modifiedApiTs: Timestamp // the modified time according to the Drive API,
                             // this can be a more up-to-date value than modifiedTs
                             // it is used for caching: when a file is opened, re-fetch only replies for comments
                             // where ApiResult.modified >  comment.modifiedApiTs
    repliesTotal: number  // from Drive Activity API, but it can be outdated.

    repliesTotalInFromTo: number // the number of replies within the folder's fromTs-toTs range
    actorsInFromTo: ActorId[] // the actors involved in the comment thread within the folder's fromTs-toTs range

    fileIcon: string // GFileAction.icon, cached for display without needing to look up the file
    fileTitle: string // GFileAction.title, cached for display without needing to look up the file

    data?: GCommentData    // Google Drive API comments.get() data, fetched on fileView opening
    dataFetchTs?: Timestamp // when the comment data was fetched, used for cache expiration etc
}

// populated from Drive API comments.get on fileView opening
export interface GCommentData {
    anchor: string  // kix.ze63akt0ri61
    content: string
    htmlContent: string
    createdTime: Timestamp
    modifiedTime: Timestamp
    deleted: boolean
    assigneeEmailAddress?: string
    mentionedEmailAddresses?: string[]
    resolved: boolean
    firstReplyTime?: Timestamp
    lastReplyTime?: Timestamp
    author: GCommentDataAuthor
    quotedFileContent?: string  // html
}

export type GFileCommentsMap = Map<CommentId, GComment> // Map<commentId, GComment>
export type GCommentsMap = Map<GoogleItemId, GFileCommentsMap> // Map<fileGid, GFileCommentsMap>

// a comment that is either missing from Dexie or only known as a ghost (see GComment.ghost),
// used to fetch its real creation/assignment data from the Drive Activity API
export type MissingComment = Pick<GComment, 'commentId' | 'createdTs'>

// populated from Drive Activity API
export interface GReply {
    // primary key: id = folderId + fileGid + commentId + replyId
    folderId: number
    fileGid: GoogleItemId
    commentId: CommentId // the parent
    replyId: CommentId // the ID of the reply (timestamp)
    replyIdOriginal: CommentId // the ID of the reply
    actor: ActorId

    ghost?: boolean // for replies fetched directly by Drive API when a file has replies newer than folder.toTs

    // lastActionType
    actionType: GActionCommentType
    deleted: boolean

    assignee?: ActorId  // the user to whom the Comment reply is assigned/reassigned, if any
    mentioned?: ActorId[]
    mentionsMe: boolean // whether I'm mentioned in this reply

    createdTs: Timestamp

    data?: GReplyData
    dataFetchTs?: Timestamp // when the reply data was fetched, used for cache expiration etc
}

// populated from Drive API comments.get:
// TODO: if not all replies are returned, use replies.list with pagination (pageToken))
//       as a test, added 100 replies to a comment thread, comments.get returns all of them
export interface GReplyData {
    action?: 'RESOLVE' | 'REOPEN' //  | 'REASSIGN' | 'ACCEPT_SUGGESTION' | 'REJECT_SUGGESTION' | 'DELETE_ACCEPT' | 'DELETE_REJECT'
    content: string
    htmlContent: string
    createdTime: Timestamp
    modifiedTime: Timestamp // Drive API
    deleted: boolean
    mentionedEmailAddresses?: string[]
    author?: GCommentDataAuthor
}

export type GFileRepliesMap = Map<CommentId, GReply[]> // Map<replyId, GReply>
export type GRepliesMap = Map<GoogleItemId, GFileRepliesMap> // Map<fileGid, GFileRepliesMap>

export interface Registry {
    key: string
    value: string | number | boolean | null
    data?: string // JSON
}
